import React, { useMemo, useState, useCallback } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import { trackAnalyticsEvent } from '../lib/analytics';
import ProgressMeter from '../components/OwnerDiagnostic/ProgressMeter';
import ValueGapQuestion from '../components/ValueGapWizard/ValueGapQuestion';
import ValueGapResults from '../components/ValueGapWizard/ValueGapResults';

const GET_CURRENT_USER_FOR_VALUE_GAP = gql`
  query GetCurrentUserForValueGap {
    getCurrentUser {
      id
      readiness_goal_type
      readiness_goal_lump_sum
      readiness_goal_annual_income
      readiness_withdrawal_rate
      fee_based_revenue
      trailer_revenue
      direct_revenue
      transactional_revenue
      recurring_revenue
      normalized_operating_expenses
      profit_gap_recasted_ebitda
      diag_readiness_total
      diag_attractiveness_total
      value_gap_current_value
      value_gap_gap_value
      value_gap_multiple_used
      value_gap_completed_at
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUserValueGap(
    $value_gap_current_value: Float,
    $value_gap_gap_value: Float,
    $value_gap_multiple_used: Float,
    $value_gap_last_updated_at: ISO8601DateTime,
    $value_gap_completed_at: ISO8601DateTime,
    $profit_gap_recasted_ebitda: Float
  ) {
    updateCurrentUser(
      value_gap_current_value: $value_gap_current_value,
      value_gap_gap_value: $value_gap_gap_value,
      value_gap_multiple_used: $value_gap_multiple_used,
      value_gap_last_updated_at: $value_gap_last_updated_at,
      value_gap_completed_at: $value_gap_completed_at,
      profit_gap_recasted_ebitda: $profit_gap_recasted_ebitda
    )
  }
`;

const Inner = ({ company }) => {
  const { data, loading, error } = useQuery(GET_CURRENT_USER_FOR_VALUE_GAP, { fetchPolicy: 'cache-and-network' });
  const [updateUser] = useMutation(UPDATE_USER);
  const [step, setStep] = useState(0);

  const user = data?.getCurrentUser || {};
  const lowMultiple = (company && company.ebitda_multiple_low != null) ? parseFloat(company.ebitda_multiple_low) : 6.0;
  const highMultiple = (company && company.ebitda_multiple_high != null) ? parseFloat(company.ebitda_multiple_high) : 10.0;

  // Wealth Goal and Annual translation
  const wealth = useMemo(() => {
    const withdrawal = (user.readiness_withdrawal_rate != null) ? parseFloat(user.readiness_withdrawal_rate) : 0.04;
    if (user.readiness_goal_type === 'lump_sum') {
      const X = Number(user.readiness_goal_lump_sum || 0);
      const Y = X * withdrawal;
      return { X, Y, withdrawal };
    } else {
      const Y = Number(user.readiness_goal_annual_income || 0);
      const X = withdrawal > 0 ? (Y / withdrawal) : 0;
      return { X, Y, withdrawal };
    }
  }, [user.readiness_goal_type, user.readiness_goal_lump_sum, user.readiness_goal_annual_income, user.readiness_withdrawal_rate]);

  // Recasted EBITDA: snapshot if present, else derive
  const totals = useMemo(() => {
    const total_revenue = (user.recurring_revenue || 0) + (user.fee_based_revenue || 0) + (user.trailer_revenue || 0) + (user.direct_revenue || 0) + (user.transactional_revenue || 0);
    const derived = Number(total_revenue) - Number(user.normalized_operating_expenses || 0);
    const recasted_ebitda = (user.profit_gap_recasted_ebitda != null) ? Number(user.profit_gap_recasted_ebitda) : derived;
    return { total_revenue, recasted_ebitda };
  }, [user.recurring_revenue, user.fee_based_revenue, user.trailer_revenue, user.direct_revenue, user.transactional_revenue, user.normalized_operating_expenses, user.profit_gap_recasted_ebitda]);

  // Multiple selection (CEPA 6-bucket)
  const selection = useMemo(() => {
    const norm = (v) => {
      const n = ((Number(v || 0) - 6.0) / 24.0);
      return Math.max(0, Math.min(1, n));
    };
    const r = norm(user.diag_readiness_total);
    const a = norm(user.diag_attractiveness_total);
    const avg = (r + a) / 2.0;
    const bucket = Math.max(0, Math.min(5, Math.floor(avg * 6)));
    const step = (highMultiple - lowMultiple) / 5.0;
    const multiple = lowMultiple + bucket * step;
    return { r, a, avg, bucket, multiple };
  }, [user.diag_readiness_total, user.diag_attractiveness_total, lowMultiple, highMultiple]);

  const currentValue = useMemo(() => (totals.recasted_ebitda || 0) * (selection.multiple || 0), [totals.recasted_ebitda, selection.multiple]);
  const valueGap = useMemo(() => (wealth.X || 0) - (currentValue || 0), [wealth.X, currentValue]);

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const persist = useCallback(async (complete = false) => {
    try {
      await updateUser({ variables: {
        value_gap_current_value: currentValue,
        value_gap_gap_value: valueGap,
        value_gap_multiple_used: selection.multiple,
        value_gap_last_updated_at: new Date().toISOString(),
        value_gap_completed_at: complete ? new Date().toISOString() : null,
        profit_gap_recasted_ebitda: totals.recasted_ebitda,
      }});
    } catch (e) {}
  }, [currentValue, valueGap, selection.multiple, totals.recasted_ebitda, updateUser]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;

  if (step === 3) {
    trackAnalyticsEvent('value_gap_step_3_viewed', { bucket: selection.bucket, multiple: selection.multiple });
    return (
      <div>
        <div className="px-4 md:px-0 max-w-3xl mx-auto mt-2">
          <ProgressMeter current={3} total={4} />
        </div>
        <ValueGapResults
          wealth={wealth}
          totals={totals}
          selection={selection}
          currentValue={currentValue}
          valueGap={valueGap}
          onBack={back}
          onComplete={async () => { await persist(true); trackAnalyticsEvent('value_gap_completed', { valueGap, currentValue, multiple: selection.multiple }); }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 md:px-0 max-w-3xl mx-auto mt-2">
        <ProgressMeter current={Math.min(step, 3)} total={4} />
      </div>
      <ValueGapQuestion
        step={step}
        wealth={wealth}
        totals={totals}
        selection={selection}
        onNext={async () => {
          if (step === 0) trackAnalyticsEvent('value_gap_step_1_viewed');
          if (step === 1) trackAnalyticsEvent('value_gap_step_2_viewed');
          if (step === 2) { await persist(false); }
          next();
        }}
        onBack={back}
      />
    </div>
  );
};

class ValueGapWizardContainer extends React.Component {
  componentDidMount() {
    trackAnalyticsEvent('value_gap_started');
  }
  render() {
    const { company } = this.props;
    return (
      <ApolloProvider client={client}>
        <Inner company={company || {}} />
      </ApolloProvider>
    );
  }
}

export default ValueGapWizardContainer;


